'use client';

import Hls from 'hls.js';
import { Loader2, Play } from 'lucide-react';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

import ControlsContainer from './common/controls-container';
import { HLSPlayerProvider, useHLSPlayer } from './contexts/hls-player-provider';

interface IHLSVideoPlayerProps {
	videoUrl: string;
	thumbnail?: string;
	className?: string;
	videoClassName?: string;
	originalFilename?: string;
	formId?: string;
	submissionId?: string;
	fieldId?: string;
	formToken?: string;
	videoMetadata?: {
		storagePath: string;
		originalFilename: string;
		videoUrl: string;
		thumbnail: string;
		mediaConvertJobId: string;
		duration?: number;
	};
	onDownloadVideoClick?: () => Promise<void>;
}

const HLS_RETRY_DELAYS_MS = [2000, 5000, 10000] as const;

export default function HLSVideoPlayer(props: IHLSVideoPlayerProps) {
	return (
		<HLSVideoPlayerInner
			key={props.videoUrl}
			{...props}
		/>
	);
}

function HLSVideoPlayerInner({
	videoUrl,
	thumbnail,
	className,
	videoClassName,
	onDownloadVideoClick,
	formId,
	submissionId,
	fieldId,
}: IHLSVideoPlayerProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const hlsRef = useRef<Hls | null>(null);
	const retryCountRef = useRef(0);
	const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [failedVideoUrl, setFailedVideoUrl] = useState<string | null>(null);
	const [reloadAttempt, setReloadAttempt] = useState(0);
	const hasError = failedVideoUrl === videoUrl;

	const trackVideoLoadFailed = useCallback(
		(reason: string, extra?: Record<string, unknown>) => {
			emitMixpanelEvent(mixpanelEvents.ACTION_VIDEO_LOAD_FAILED, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formId,
				submission_id: submissionId,
				field_id: fieldId,
				video_url: videoUrl,
				reason,
				...extra,
			});
		},
		[fieldId, formId, submissionId, videoUrl]
	);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		setIsLoading(true);
		let failureHandled = false;

		const handleLoadFailure = (
			reason: string,
			extra?: Record<string, unknown>,
			retryable = true
		) => {
			if (failureHandled) return;
			failureHandled = true;

			const retryDelay = retryable
				? HLS_RETRY_DELAYS_MS[retryCountRef.current]
				: undefined;
			trackVideoLoadFailed(reason, {
				...extra,
				retry_attempt: retryCountRef.current + 1,
				retry_scheduled: retryDelay !== undefined,
			});

			if (retryDelay !== undefined) {
				retryCountRef.current += 1;
				retryTimeoutRef.current = setTimeout(() => {
					setReloadAttempt((attempt) => attempt + 1);
				}, retryDelay);
				return;
			}

			setFailedVideoUrl(videoUrl);
			setIsLoading(false);
		};

		// Local recordings (object URLs) are plain video files, not HLS manifests
		if (videoUrl.startsWith('blob:')) {
			video.src = videoUrl;
			const handleLoadedMetadata = () => setIsLoading(false);
			const handleNativeError = () => handleLoadFailure('local_video_error', undefined, false);
			video.addEventListener('loadedmetadata', handleLoadedMetadata);
			video.addEventListener('error', handleNativeError);

			return () => {
				video.removeEventListener('loadedmetadata', handleLoadedMetadata);
				video.removeEventListener('error', handleNativeError);
			};
		}

		// Initialize HLS.js for streaming URLs
		if (Hls.isSupported()) {
			const hls = new Hls({
				enableWorker: true,
				lowLatencyMode: false,
				backBufferLength: 90,
			});
			hlsRef.current = hls;

			hls.loadSource(videoUrl);
			hls.attachMedia(video);

			hls.on(Hls.Events.MANIFEST_PARSED, () => {
				setIsLoading(false);
			});

			hls.on(Hls.Events.ERROR, (event, data) => {
				console.error('HLS Error:', data);
				console.error('Video URL that failed:', videoUrl);
				if (data.fatal) {
					hls.stopLoad();
					handleLoadFailure('hls_fatal_error', {
						hls_error_type: data.type,
						hls_error_details: data.details,
					});
				}
			});
		} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
			// Native HLS support (Safari)
			console.log('Using native HLS support for video URL:', videoUrl);
			video.src = videoUrl;
			const handleCanPlay = () => setIsLoading(false);
			const handleNativeError = (event: Event) => {
				console.error('Native video error:', event);
				console.error('Video URL that failed:', videoUrl);
				handleLoadFailure('native_hls_error');
			};
			video.addEventListener('canplay', handleCanPlay);
			video.addEventListener('error', handleNativeError);

			return () => {
				video.removeEventListener('canplay', handleCanPlay);
				video.removeEventListener('error', handleNativeError);
				if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
			};
		} else {
			console.error('HLS not supported and no native support available');
			handleLoadFailure('hls_not_supported', undefined, false);
		}

		return () => {
			if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
			hlsRef.current?.destroy();
			hlsRef.current = null;
		};
	}, [reloadAttempt, trackVideoLoadFailed, videoUrl]);

	const handlePlay = () => {
		const video = videoRef.current;
		if (!video) return;

		video.play();
		setHasStartedPlaying(true);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handlePlay();
		}
	};

	if (hasError) {
		return (
			<div
				className={cn(
					'flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-8',
					className
				)}
			>
				<div className="text-center text-gray-500">Unable to load video</div>
			</div>
		);
	}

	return (
		<HLSPlayerProvider
			videoRef={videoRef}
			containerRef={containerRef}
			onDownloadVideoClick={onDownloadVideoClick}
		>
			<VideoContainerInner
				className={className}
				videoClassName={videoClassName}
				thumbnail={thumbnail}
				hasStartedPlaying={hasStartedPlaying}
				isLoading={isLoading}
				handlePlay={handlePlay}
				handleKeyDown={handleKeyDown}
			/>
		</HLSPlayerProvider>
	);
}

type VideoContainerProps = {
	className?: string;
	videoClassName?: string;
	thumbnail?: string;
	hasStartedPlaying: boolean;
	isLoading: boolean;
	handlePlay: () => void;
	handleKeyDown: (e: React.KeyboardEvent) => void;
};

function VideoContainerInner({
	className,
	videoClassName,
	thumbnail,
	hasStartedPlaying,
	isLoading,
	handlePlay,
	handleKeyDown,
}: VideoContainerProps) {
	const { containerRef, videoRef, handleMouseMove, handleMouseLeave, togglePlay } = useHLSPlayer();

	return (
		<div
			ref={containerRef}
			className={cn(
				'relative overflow-hidden rounded-lg border border-gray-200 bg-black',
				className
			)}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
		>
			{/* Thumbnail overlay when not started playing */}
			{thumbnail && !hasStartedPlaying && (
				<div
					className="group absolute inset-0 z-10 cursor-pointer"
					onClick={handlePlay}
					onKeyDown={handleKeyDown}
					role="button"
					tabIndex={0}
					aria-label="Play video"
				>
					<img
						src={thumbnail}
						alt="Video thumbnail"
						className="h-full w-full object-cover"
					/>
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="absolute inset-0 bg-primary opacity-10 transition-colors group-hover:opacity-20" />
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-md transition-all">
							<Play
								size={16}
								className="ml-0.5 text-primary"
							/>
						</div>
					</div>
				</div>
			)}

			{/* Video element */}
			<video
				ref={videoRef}
				className={cn('h-full w-full cursor-pointer object-cover', videoClassName)}
				playsInline
				controls={false}
				controlsList="nodownload"
				onPlay={() => {}}
				onClick={togglePlay}
			>
				<track kind="captions" />
			</video>

			{/* Custom controls - without a thumbnail overlay, the video is playable right away */}
			{(hasStartedPlaying || !thumbnail) && <ControlsContainer />}

			{/* Simple loading indicator */}
			{isLoading && !hasStartedPlaying && (
				<div className="absolute inset-0 flex items-center justify-center bg-gray-100">
					<Loader2 className="h-8 w-8 animate-spin" />
				</div>
			)}
		</div>
	);
}
